const REFUSAL_PHRASES = [
  "not permitted",
  "not allowed",
  "not permited",
  "non-changeable",
  "non changeable",
  "not changeable",
  "non-refundable",
  "non refundable",
  "not refundable",
  "prohibited",
  "no changes",
  "not available",
];

const PERMITTED_PHRASES = [
  "permitted",
  "allowed",
  "changeable",
  "refundable",
];

const EMPTY_FEE_VALUES = new Set([
  "",
  "n/a",
  "na",
  "nil",
  "none",
  "-",
  "--",
]);

export function hasFormattedFareRules(rules) {
  return Boolean(rules && typeof rules === "object" && Object.keys(rules).length);
}

export function getFlightBookingFromChangeData(data) {
  const raw = data?.flightBooking ?? data?.flightObj;
  if (Array.isArray(raw)) return raw[0] || null;
  return raw || null;
}

export function getMiniRulesList(miniRules) {
  if (Array.isArray(miniRules?.rules)) return miniRules.rules;
  if (Array.isArray(miniRules)) return miniRules;
  return [];
}

export function getRawFareRulesText(...candidates) {
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) return value;
  }
  return "";
}

export function isEmptyFeeValue(value) {
  if (value == null) return true;
  const normalized = String(value).trim().toLowerCase();
  return !normalized || EMPTY_FEE_VALUES.has(normalized);
}

export function capitalizePolicySentence(text = "") {
  const value = String(text || "").trim();
  if (!value) return "";
  return value
    .split(". ")
    .map((part) => {
      const trimmed = part.trim();
      if (!trimmed) return "";
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    })
    .filter(Boolean)
    .join(". ");
}

function textContainsAny(text, phrases) {
  const haystack = String(text || "").toLowerCase();
  if (!haystack) return false;
  return phrases.some((phrase) => haystack.includes(phrase));
}

function findMiniRule(miniRules, category) {
  return getMiniRulesList(miniRules).find(
    (rule) => String(rule?.category || "").toUpperCase() === category,
  );
}

function evaluatePolicyStatus(beforeDepartureText, miniRule) {
  const refuses = textContainsAny(beforeDepartureText, REFUSAL_PHRASES);
  if (refuses) return "restricted";
  if (miniRule?.notApplicable === true) return "restricted";
  if (miniRule?.notApplicable === false) return "permitted";
  if (
    textContainsAny(beforeDepartureText, PERMITTED_PHRASES) &&
    !refuses
  ) {
    return "permitted";
  }
  return "unknown";
}

function pickPenalty(...candidates) {
  for (const value of candidates) {
    if (!isEmptyFeeValue(value)) return String(value).trim();
  }
  return "";
}

/**
 * Decide whether a date change looks permitted from formatted fare rules + miniRules.
 * A failed format/read is not a refusal — caller should use status "unread".
 */
export function getChangeFareRuleInsight({
  formattedFareRules,
  miniRules,
  readFailed = false,
} = {}) {
  if (readFailed) {
    return {
      status: "unread",
      label: "Could not be read",
      detail:
        "Fare rules could not be read. That is not a refusal — you can still send the change.",
      changePenalty: "",
      noShowPenalty: "",
      refundStatus: "unread",
      refundPenalty: "",
    };
  }

  const reissue = formattedFareRules?.reissue || {};
  const refund = formattedFareRules?.refund || {};
  const noShow = formattedFareRules?.noShow || {};
  const exchangeRule = findMiniRule(miniRules, "EXCHANGE");
  const refundRule = findMiniRule(miniRules, "REFUND");

  const changeStatus = evaluatePolicyStatus(
    reissue?.reissuePolicyBeforeDeparture,
    exchangeRule,
  );
  const refundStatus = evaluatePolicyStatus(
    refund?.refundPolicyBeforeDeparture,
    refundRule,
  );

  const changePenalty = pickPenalty(
    reissue?.reissueFee,
    exchangeRule?.maxPenaltyAmount,
  );
  const noShowPenalty = pickPenalty(
    noShow?.noShowFee,
    noShow?.noShowPolicyBeforeDeparture,
  );
  const refundPenalty = pickPenalty(
    refund?.refundFee,
    refundRule?.maxPenaltyAmount,
  );

  const labels = {
    permitted: "Change permitted",
    restricted: "Change restricted",
    unknown: "Change status unclear",
  };

  const details = {
    permitted: changePenalty
      ? `Airline rules allow a change. Penalty in rules: ${changePenalty}.`
      : "Airline rules allow a change.",
    restricted:
      "Airline rules look restricted for changes. Check the full fare rules before you continue.",
    unknown:
      "Rules do not clearly say yes or no. A fee alone is not a decision — open the fare rules.",
  };

  return {
    status: changeStatus,
    label: labels[changeStatus],
    detail: details[changeStatus],
    changePenalty,
    noShowPenalty,
    refundStatus,
    refundPenalty,
  };
}

export function buildFareRuleSectionBody(block, { beforeKey, afterKey, feeKey } = {}) {
  if (!block || typeof block !== "object") return null;

  const before = capitalizePolicySentence(block?.[beforeKey]);
  const after = capitalizePolicySentence(block?.[afterKey]);
  const fee = feeKey && !isEmptyFeeValue(block?.[feeKey])
    ? String(block[feeKey]).trim()
    : "";

  if (!before && !after && !fee) return null;

  return { before, after, fee };
}
